import { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import * as Sentry from '@sentry/react';
import { CONSENT_SAFETY_TIMEOUT_MS } from 'App/constants';
import { consoleInfoLog, isProd } from 'App/Helpers';
import { captureCustomException } from 'App/Services/ErrorTracking/captureCustomException';
import { installConsentErrorHandler } from 'App/Services/ErrorTracking/installConsentErrorHandler';

const SDK_ID = 'cookieyes';
const MAX_RETRIES = 1;
const SCRIPT_RETRY_DELAY_MS = 400;

const parseConsentCookie = () => {
    try {
        const raw = document.cookie
            .split(';')
            .find((c) => c.trim().startsWith('cookieyes-consent='));

        if (!raw) return null;

        return raw
            .split('=')[1]
            .split(',')
            .reduce((acc, pair) => {
                const [key, value] = pair.split(':');

                acc[key] = value;

                return acc;
            }, {});
    } catch (error) {
        captureCustomException(error, 'ConsentError');
        return null;
    }
};

/**
 * Reads one consent category, keeping "not answered yet" distinct from "no".
 *
 * Two states look like a refusal but are not one:
 *
 * - CookieYes writes `cookieyes-consent` as soon as it mints a consent id,
 *   which happens on the first load before the banner has been answered, and
 *   every category is empty at that point.
 * - Where the banner applies, CookieYes then pre-fills every optional category
 *   with `'no'` and leaves `action` empty until the user acts.
 *
 * Treating either as a decline made consumers act on a refusal the user never
 * made and then undo it once the real answer arrived. A `'no'` therefore only
 * counts once `action` is set. A `'yes'` is always a grant, which also covers
 * the implied consent path (`action: 'no'`, every category `'yes'`).
 *
 * @param {string | undefined} value - Raw category value from the cookie.
 * @param {string | undefined} action - Raw `action` value from the cookie.
 * @returns {boolean | undefined} - `undefined` while still unanswered.
 */
const readConsentCategory = (value, action) => {
    if (value === 'yes') return true;
    if (value === 'no' && action) return false;

    return undefined;
};

const bannerIsHidden = () => {
    const el = document.body.querySelector('.cky-consent-container');
    return !el || el.classList.contains('cky-hide');
};

const reportLocalFail = (localUrl) => {
    if (!isProd()) return;

    Sentry.captureMessage('CookieYes local SDK failed; falling back to remote', {
        level: 'warning',
        tags: { module: 'consent', consent_url: localUrl },
    });
};

const createScript = (id, src, { onLoad, onError }) => {
    const el = document.createElement('script');

    el.id = id;
    el.defer = true;
    el.referrerPolicy = 'strict-origin-when-cross-origin';
    el.src = src;
    el.onerror = onError;
    el.onload = onLoad;
    document.head.appendChild(el);

    return el;
};

const removeScript = (el) => {
    if (el?.parentNode) el.parentNode.removeChild(el);
};

const reportConsentLoadFailure = (localFailed, remoteFailed, localSdkUrl) => {
    if (!isProd()) return;

    const error = new Error(
        remoteFailed
            ? 'CookieYes SDK failed to load (local and remote)'
            : 'Local CookieYes SDK failed, trying remote'
    );
    error.name = remoteFailed ? 'ConsentSDKLoadError' : 'LocalConsentError';

    Sentry.captureException(error, {
        tags: {
            module: 'consent',
            local_failed: String(localFailed),
            remote_failed: String(remoteFailed),
            local_url: localSdkUrl,
        },
    });
};

const ConsentContext = createContext(null);

/**
 * Loads the CookieYes consent SDK (local first, remote as fallback), tracks the user's consent
 * cookie and exposes the consent state through `ConsentContext`.
 *
 * @param {object} props - Component props.
 * @param {string} props.remoteSDKUrl - CDN URL of the CookieYes SDK, used as the fallback or when `sdkSource` is "remote".
 * @param {string} [props.localSdkUrl] - Path of the self-hosted CookieYes SDK on the landing origin, tried first.
 * @param {string} [props.sdkSource] - Set to "remote" to load only the CDN SDK; otherwise the local SDK is tried first.
 * @param {import("react").ReactNode} props.children - Landing tree that reads the consent state.
 * @returns {import("react").JSX.Element} - The context provider wrapping `children`.
 */
const ConsentProvider = ({ remoteSDKUrl, localSdkUrl, sdkSource, children }) => {
    const [consentBannerReady, setConsentBannerReady] = useState(false);
    const [performanceConsent, setPerformanceConsent] = useState(undefined);
    const [functionalConsent, setFunctionalConsent] = useState(undefined);
    const [analyticsConsent, setAnalyticsConsent] = useState(undefined);
    const [consentUserAction, setConsentUserAction] = useState(undefined);
    const [consentId, setConsentId] = useState(undefined);

    const updateConsent = useCallback(() => {
        const parsed = parseConsentCookie();
        if (!parsed) return;

        setConsentId(parsed.consentid);
        setConsentUserAction(parsed.action === 'yes');
        setPerformanceConsent(readConsentCategory(parsed.performance, parsed.action));
        setFunctionalConsent(readConsentCategory(parsed.functional, parsed.action));
        setAnalyticsConsent(readConsentCategory(parsed.analytics, parsed.action));
    }, []);

    useLayoutEffect(() => {
        const onBannerLoad = () => {
            if (bannerIsHidden()) {
                setConsentBannerReady(true);
                return;
            }

            setConsentBannerReady(true);
        };
        document.addEventListener('cookieyes_banner_load', onBannerLoad);

        return () => document.removeEventListener('cookieyes_banner_load', onBannerLoad);
    }, []);

    useEffect(() => {
        updateConsent();
        document.addEventListener('cookieyes_consent_update', updateConsent);

        return () => document.removeEventListener('cookieyes_consent_update', updateConsent);
    }, [updateConsent]);

    useEffect(() => {
        if (typeof document === 'undefined' || window.__PUPPETEER_PREBUILD_MODE__) return;

        const cancelledRef = { current: false };
        const localSdk = localSdkUrl ? `${window.location.origin}${localSdkUrl}` : null;

        // Catch nested banner script / CDN asset failures (DevTools-only otherwise).
        // Must run before the SDK loads so fetch wrapping is bound by CookieYes.
        const uninstallConsentErrorHandler = installConsentErrorHandler();

        if (document.getElementById(SDK_ID)) {
            return () => uninstallConsentErrorHandler();
        }

        const tryLoadRemote = (retryCount = 0) => {
            if (cancelledRef.current) return;

            const script = createScript(SDK_ID, remoteSDKUrl, {
                onLoad: () => {
                    if (cancelledRef.current) return;
                    consoleInfoLog('## Init: Remote CookieYes SDK loaded', 'init');
                },
                onError: () => {
                    removeScript(script);
                    if (cancelledRef.current) return;

                    if (retryCount < MAX_RETRIES) {
                        setTimeout(() => tryLoadRemote(retryCount + 1), SCRIPT_RETRY_DELAY_MS);
                        return;
                    }

                    reportConsentLoadFailure(true, true, localSdkUrl);
                    setConsentBannerReady(true);
                },
            });
        };

        const tryLoadLocal = (retryCount = 0) => {
            if (cancelledRef.current) return;

            const script = createScript(SDK_ID, localSdk, {
                onLoad: () => {
                    if (cancelledRef.current) return;
                    consoleInfoLog('## Init: Local CookieYes SDK loaded', 'init');
                },
                onError: () => {
                    removeScript(script);
                    if (cancelledRef.current) return;

                    if (retryCount < MAX_RETRIES) {
                        setTimeout(() => tryLoadLocal(retryCount + 1), SCRIPT_RETRY_DELAY_MS);
                        return;
                    }

                    reportLocalFail(localSdk);
                    tryLoadRemote(0);
                },
            });
        };

        // Dev: sdkSource "remote" loads CDN only. Unset / "local" keeps local-first.
        if (sdkSource === 'remote') {
            tryLoadRemote(0);
        } else {
            tryLoadLocal();
        }

        // safety-timeout for unhandled errors
        const timeout = setTimeout(() => setConsentBannerReady(true), CONSENT_SAFETY_TIMEOUT_MS);
        return () => {
            cancelledRef.current = true;
            clearTimeout(timeout);
            uninstallConsentErrorHandler();
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        window.dataLayer = window.dataLayer || [];
        function gtag() {
            window.dataLayer.push(arguments);
        }

        gtag('consent', 'default', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
            functionality_storage: 'denied',
            personalization_storage: 'denied',
            security_storage: 'granted',
            wait_for_update: 2000,
        });

        gtag('set', 'ads_data_redaction', true);
        gtag('set', 'url_passthrough', true);
    }, []);

    const value = useMemo(
        () => ({
            consentId,
            consentUserAction,
            consentBannerReady,
            performanceConsent,
            analyticsConsent,
            functionalConsent,
        }),
        [
            consentId,
            consentUserAction,
            consentBannerReady,
            performanceConsent,
            analyticsConsent,
            functionalConsent,
        ]
    );

    return <ConsentContext value={value}>{children}</ConsentContext>;
};

export { ConsentProvider, ConsentContext };
